import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  HostListener,
  OnInit
} from '@angular/core';
import { DialogRef } from '@ngneat/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { take, tap } from 'rxjs/operators';
import { APP_SPINNER_NAME } from '#common/constants/top-front';
import { RepoTypeEnum } from '#common/enums/repo-type.enum';
import { ResponseInfoStatusEnum } from '#common/enums/response-info-status.enum';
import { ToBackendRequestInfoNameEnum } from '#common/enums/to/to-backend-request-info-name.enum';
import type { DashboardUnit } from '#common/zod/backend/dashboard-unit';
import type {
  ToBackendDeleteDashboardRequestPayload,
  ToBackendDeleteDashboardResponse
} from '#common/zod/to-backend/dashboards/to-backend-delete-dashboard';
import { DashboardQuery } from '#front/app/queries/dashboard.query';
import { DashboardUnitsQuery } from '#front/app/queries/dashboard-units.query';
import { ApiService } from '#front/app/services/api.service';
import { NavigateService } from '#front/app/services/navigate.service';
import { UiService } from '#front/app/services/ui.service';

export interface DeleteDashboardDialogData {
  apiService: ApiService;
  dashboardUnit: DashboardUnit;
  projectId: string;
  repoId: string;
  branchId: string;
  envId: string;
  repoType: RepoTypeEnum;
  isStartSpinnerUntilNavEnd: boolean;
}

@Component({
  selector: 'm-delete-dashboard-dialog',
  templateUrl: './delete-dashboard-dialog.component.html',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule]
})
export class DeleteDashboardDialogComponent implements OnInit {
  @HostListener('window:keyup.esc')
  onEscKeyUp() {
    this.ref.close();
  }

  constructor(
    public ref: DialogRef<DeleteDashboardDialogData>,
    private spinner: NgxSpinnerService,
    private dashboardUnitsQuery: DashboardUnitsQuery,
    private dashboardQuery: DashboardQuery,
    private navigateService: NavigateService,
    private uiService: UiService
  ) {}

  ngOnInit(): void {
    setTimeout(() => {
      (document.activeElement as HTMLElement).blur();
    }, 0);
  }

  delete() {
    if (this.ref.data.isStartSpinnerUntilNavEnd === true) {
      this.spinner.show(APP_SPINNER_NAME);
    }

    this.ref.close();

    let { projectId, branchId, repoId } = this.ref.data;

    let dashboardUnit: DashboardUnit = this.ref.data.dashboardUnit;
    let apiService: ApiService = this.ref.data.apiService;

    let payload: ToBackendDeleteDashboardRequestPayload = {
      projectId: projectId,
      branchId: branchId,
      envId: this.ref.data.envId,
      repoId: repoId,
      dashboardId: dashboardUnit.dashboardId
    };

    apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendDeleteDashboard,
        payload: payload,
        showSpinner: !this.ref.data.isStartSpinnerUntilNavEnd
      })
      .pipe(
        tap((resp: ToBackendDeleteDashboardResponse) => {
          if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
            this.dashboardUnitsQuery.update({
              dashboardUnitDrafts: resp.payload.dashboardUnitDrafts,
              dashboardSpaceNodes: resp.payload.dashboardSpaceNodes
            });

            let currentDashboard = this.dashboardQuery.getValue();

            if (currentDashboard.dashboardId === dashboardUnit.dashboardId) {
              this.uiService.clearProjectDashboardLink();
              this.navigateService.navigateToDashboards();
            }
          }
        }),
        take(1)
      )
      .subscribe();
  }

  cancel() {
    this.ref.close();
  }
}
